// #607 F2: Dialog „Freigeben“ – ein oder mehrere Dokumente an Mieter und/oder Eigentümer im Portal freigeben.
// Je Portal: „Alle … des Objekts“ (dynamisch, auch für Nachfolger) oder einzelne Personen / ganze Einheiten
// (persönlich, endet mit Auszug bzw. Verkauf). Liste nach Objekt und Einheit, Suche, „ehemalige anzeigen“,
// Zusammenfassung vor dem Bestätigen, Sicherheitsabfrage bei persönlichen Dokumenten. Es wird nichts verschickt.
import { useEffect, useMemo, useState } from 'react';
import { Buildings, CircleNotch, Globe, House, MagnifyingGlass, UserCircle, Warning } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton, SchalterZeile } from '../immobilien/ui';
import { Segment } from '../ui/Segment';
import { AuswahlKaestchen } from '../ui/AuswahlKaestchen';
import {
  LEERE_AUSWAHL, auswahlAus, auswahlText, erreichtePersonen, istLeer, istPersoenlich, ladeEmpfaenger, ladeFreigaben,
  setzeFreigaben, zusammenfassung, type EmpfPerson, type Empfaenger, type FreigabeDok, type FreigabePortal, type PortalAuswahl,
} from '../../lib/dokumente/freigaben';

const WORT: Record<FreigabePortal, { einzahl: string; mehrzahl: string }> = {
  mieter: { einzahl: 'Mieter', mehrzahl: 'Mieter' },
  eigentuemer: { einzahl: 'Eigentümer', mehrzahl: 'Eigentümer' },
};

function umschalten(liste: readonly string[], id: string): string[] {
  return liste.includes(id) ? liste.filter(x => x !== id) : [...liste, id];
}

export function FreigabeDialog({ dokumente, aendern = false, onSchliessen, onFertig }: {
  dokumente: readonly FreigabeDok[];
  /** true = bisherige Freigaben werden ersetzt (bei einem Dokument mit dessen Freigaben vorbelegt) */
  aendern?: boolean;
  onSchliessen: () => void;
  onFertig: (meldung: string) => void;
}) {
  const [empf, setEmpf] = useState<Empfaenger | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [portal, setPortal] = useState<FreigabePortal>('mieter');
  const [auswahl, setAuswahl] = useState<Record<FreigabePortal, PortalAuswahl>>({ mieter: LEERE_AUSWAHL, eigentuemer: LEERE_AUSWAHL });
  const [suche, setSuche] = useState('');
  const [ehemalige, setEhemalige] = useState(false);
  const [trotzdem, setTrotzdem] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const objektIds = useMemo(() => [...new Set(dokumente.map(d => d.objekt_id).filter((x): x is string => !!x))], [dokumente]);
  const einzeln = dokumente.length === 1 ? dokumente[0]! : null;

  useEffect(() => {
    let aktiv = true;
    void (async () => {
      try {
        const [e, bisher] = await Promise.all([ladeEmpfaenger(dokumente), aendern && einzeln ? ladeFreigaben([einzeln.id]) : Promise.resolve([])]);
        if (!aktiv) return;
        setEmpf(e);
        if (aendern && einzeln) setAuswahl({ mieter: auswahlAus(bisher, 'mieter'), eigentuemer: auswahlAus(bisher, 'eigentuemer') });
        else if (einzeln) {
          // Vorschlag: die zugeordnete Person (z. B. Mietvertrag → nur dieser Mieter)
          setAuswahl({
            mieter: einzeln.mieter_id ? { ...LEERE_AUSWAHL, personen: [einzeln.mieter_id] } : LEERE_AUSWAHL,
            eigentuemer: einzeln.eigentuemer_id ? { ...LEERE_AUSWAHL, personen: [einzeln.eigentuemer_id] } : LEERE_AUSWAHL,
          });
          if (!einzeln.mieter_id && einzeln.eigentuemer_id) setPortal('eigentuemer');
        }
      } catch (err) {
        if (aktiv) setLadeFehler(err instanceof Error ? err.message : 'Empfänger konnten nicht geladen werden');
      }
    })();
    return () => { aktiv = false; };
  }, [dokumente, aendern, einzeln]);

  const personenVon = (p: FreigabePortal): EmpfPerson[] => (empf ? (p === 'mieter' ? empf.mieter : empf.eigentuemer) : []);
  const erreicht = {
    mieter: erreichtePersonen(auswahl.mieter, personenVon('mieter'), objektIds),
    eigentuemer: erreichtePersonen(auswahl.eigentuemer, personenVon('eigentuemer'), objektIds),
  };
  const teile = (['mieter', 'eigentuemer'] as const).map(p => auswahlText(p, auswahl[p], erreicht[p].size));
  const persoenlich = dokumente.filter(istPersoenlich);
  const breit = (['mieter', 'eigentuemer'] as const).some(p => auswahl[p].alle) || erreicht.mieter.size + erreicht.eigentuemer.size > 1;
  const warnen = persoenlich.length > 0 && breit;
  const nichtsGewaehlt = istLeer(auswahl.mieter) && istLeer(auswahl.eigentuemer);
  const ohneObjektAlle = (['mieter', 'eigentuemer'] as const).some(p => auswahl[p].alle) && dokumente.some(d => !d.objekt_id);

  const setze = (p: FreigabePortal, neu: Partial<PortalAuswahl>) => { setAuswahl(a => ({ ...a, [p]: { ...a[p], ...neu } })); setTrotzdem(false); };
  const a = auswahl[portal];

  // Liste: nach Objekt → Einheit gruppiert; Personen ohne Einheit (z. B. MV-Eigentümer des ganzen Objekts) je Objekt unten.
  const q = suche.trim().toLowerCase();
  const sichtbar = (x: EmpfPerson) => (ehemalige || x.aktuell || a.personen.includes(x.id)) && (!q || x.name.toLowerCase().includes(q));
  const gruppen = useMemo(() => {
    if (!empf) return [];
    const leute = (portal === 'mieter' ? empf.mieter : empf.eigentuemer);
    const objekte = empf.objekte.length ? empf.objekte : [{ id: '', titel: 'Ohne Objekt' }];
    return objekte.map(o => {
      const einheiten = empf.einheiten.filter(e => e.objektId === o.id).map(e => ({
        einheit: e,
        personen: leute.filter(x => x.einheitIds.includes(e.id)),
      })).filter(g => g.personen.length > 0);
      const ohne = leute.filter(x => (o.id ? x.objektIds.includes(o.id) : true)
        && !x.einheitIds.some(id => empf.einheiten.some(e => e.id === id && e.objektId === o.id)));
      return { objekt: o, einheiten, ohne };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empf, portal, q]);
  const zusatz = !empf || !objektIds.length ? [] : personenVon(portal).filter(x => x.objektIds.length === 0 || !x.objektIds.some(o => objektIds.includes(o)));
  const alleSichtbaren = [...new Set([
    ...gruppen.flatMap(g => [...g.einheiten.flatMap(e => e.personen), ...g.ohne]),
    ...zusatz,
  ].filter(sichtbar).map(x => x.id))];
  const alleGewaehlt = alleSichtbaren.length > 0 && alleSichtbaren.every(id => a.personen.includes(id));

  const speichern = async () => {
    if (warnen && !trotzdem) { setFehler('Bitte die Sicherheitsabfrage bestätigen.'); return; }
    setLaeuft(true); setFehler(null);
    const ids = dokumente.map(d => d.id);
    for (const p of ['mieter', 'eigentuemer'] as const) {
      const f = await setzeFreigaben(ids, p, auswahl[p], aendern);
      if (f) { setFehler(f); setLaeuft(false); return; }
    }
    setLaeuft(false);
    onFertig(nichtsGewaehlt ? 'Freigaben entzogen' : zusammenfassung(dokumente.length, teile, false).replace(/ freigeben$/, ' freigegeben'));
  };

  const personZeile = (x: EmpfPerson, einruecken: boolean) => {
    const an = a.personen.includes(x.id);
    const durchAlle = a.alle && x.aktuell;
    return (
      <div key={x.id} className="flex items-center" style={{ gap: 10, padding: '6px 4px', paddingLeft: einruecken ? 30 : 4, opacity: durchAlle && !an ? 0.55 : 1 }}>
        <AuswahlKaestchen an={an || durchAlle} gesperrt={durchAlle && !an} label={`${x.name} auswählen`}
          tip={durchAlle && !an ? 'Schon über „Alle“ freigegeben' : undefined}
          onUmschalten={() => setze(portal, { personen: umschalten(a.personen, x.id) })} />
        <UserCircle size={16} weight="duotone" color="var(--ink3)" />
        <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13, fontWeight: 600 }}>{x.name}</span>
        {!x.aktuell && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>ehemalig</span>}
      </div>
    );
  };

  const titel = aendern ? 'Freigabe ändern' : 'Freigeben';
  const untertitel = einzeln ? einzeln.titel : `${dokumente.length} Dokumente`;

  return (
    <DialogHuelle titel={titel} untertitel={untertitel} icon={Globe} onSchliessen={onSchliessen} laeuft={laeuft} breite={620}
      fuss={(
        <>
          <span className="flex-1" style={{ fontSize: 13, fontWeight: 700, minWidth: 200, alignSelf: 'center' }}>
            {zusammenfassung(dokumente.length, teile, aendern)}
          </span>
          <ImmoButton onClick={onSchliessen} disabled={laeuft}>Abbrechen</ImmoButton>
          <ImmoButton art="akzent" icon={Globe} disabled={laeuft || !empf || (nichtsGewaehlt && !aendern) || (warnen && !trotzdem)}
            onClick={() => { void speichern(); }}>
            {laeuft ? 'Speichert …' : nichtsGewaehlt && aendern ? 'Freigaben entziehen' : aendern ? 'Freigabe speichern' : 'Freigeben'}
          </ImmoButton>
        </>
      )}>
      {ladeFehler && <span role="alert" style={{ fontSize: 13, color: 'var(--bad)' }}>{ladeFehler}</span>}
      {!empf && !ladeFehler && <CircleNotch size={22} className="animate-spin" color="var(--ink3)" aria-label="Lädt" />}
      {empf && (
        <>
          <Segment ariaLabel="Portal" voll hoehe={34} wert={portal} onChange={(p) => { setPortal(p); setSuche(''); }}
            eintraege={(['mieter', 'eigentuemer'] as const).map(p => ({ key: p, label: `${WORT[p].mehrzahl}${auswahl[p].alle ? ' · alle' : erreicht[p].size ? ` · ${erreicht[p].size}` : ''}` }))} />

          <div className="flex items-center" style={{ gap: 10, padding: '10px 12px', borderRadius: 14, background: a.alle ? 'var(--acc-soft)' : 'var(--fill)' }}>
            <AuswahlKaestchen an={a.alle} label={`Alle ${WORT[portal].mehrzahl} des Objekts`} gesperrt={objektIds.length === 0}
              tip={objektIds.length === 0 ? 'Das Dokument gehört zu keinem Objekt' : undefined}
              onUmschalten={() => setze(portal, { alle: !a.alle })} />
            <span className="flex flex-col flex-1 min-w-0">
              <span style={{ fontSize: 14, fontWeight: 800 }}>
                Alle {WORT[portal].mehrzahl} {objektIds.length > 1 ? 'des jeweiligen Objekts' : 'des Objekts'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                Allgemeine Unterlagen (z. B. Hausordnung, Versicherung): auch neue {WORT[portal].mehrzahl} sehen es automatisch{portal === 'eigentuemer' ? ', auch rückwirkend' : ''}.
              </span>
            </span>
          </div>
          {ohneObjektAlle && <span style={{ fontSize: 12, color: 'var(--warn, var(--ink3))' }}>Dokumente ohne Objekt bekommen keine Freigabe an „Alle“.</span>}

          <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
            <label className="flex items-center flex-1" style={{ gap: 8, minWidth: 200, height: 40, padding: '0 12px', borderRadius: 12, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
              <MagnifyingGlass size={15} color="var(--ink3)" />
              <input value={suche} onChange={e => setSuche(e.target.value)} placeholder={`${WORT[portal].einzahl} suchen`} aria-label={`${WORT[portal].einzahl} suchen`}
                className="flex-1 min-w-0 outline-none" style={{ border: 'none', background: 'transparent', fontSize: 13, color: 'var(--ink)' }} />
            </label>
            <ImmoButton onClick={() => setze(portal, { personen: alleGewaehlt ? a.personen.filter(id => !alleSichtbaren.includes(id)) : [...new Set([...a.personen, ...alleSichtbaren])] })}
              disabled={alleSichtbaren.length === 0}>
              {alleGewaehlt ? 'Auswahl aufheben' : 'Alle auswählen'}
            </ImmoButton>
          </div>
          <SchalterZeile klein label="Ehemalige anzeigen" icon={UserCircle} an={ehemalige} onChange={setEhemalige} />

          <div className="flex flex-col" style={{ gap: 4 }}>
            {gruppen.map(g => (
              <div key={g.objekt.id || 'ohne'} className="flex flex-col" style={{ gap: 2 }}>
                {gruppen.length > 1 && (
                  <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 800, color: 'var(--ink3)', padding: '8px 4px 2px' }}>
                    <Buildings size={14} weight="fill" />{g.objekt.titel}
                  </span>
                )}
                {g.einheiten.map(({ einheit, personen }) => {
                  const sicht = personen.filter(sichtbar);
                  const anE = a.einheiten.includes(einheit.id);
                  if (sicht.length === 0 && !anE) return null;
                  return (
                    <div key={einheit.id} className="flex flex-col" style={{ borderRadius: 12, background: anE ? 'var(--acc-soft)' : 'transparent' }}>
                      <div className="flex items-center" style={{ gap: 10, padding: '6px 4px' }}>
                        <AuswahlKaestchen an={anE} label={`Ganze Einheit ${einheit.bezeichnung}`}
                          tip="Alle, die heute in der Einheit wohnen bzw. ihr gehören – Nachfolger sehen es nicht"
                          onUmschalten={() => setze(portal, { einheiten: umschalten(a.einheiten, einheit.id) })} />
                        <House size={15} weight="fill" color="var(--ink3)" />
                        <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13, fontWeight: 800 }}>{einheit.bezeichnung}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>ganze Einheit</span>
                      </div>
                      {sicht.map(x => personZeile(x, true))}
                    </div>
                  );
                })}
                {g.ohne.filter(sichtbar).length > 0 && (
                  <>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', padding: '6px 4px 0' }}>
                      {portal === 'eigentuemer' ? 'Eigentümer des ganzen Objekts' : 'Ohne Einheit'}
                    </span>
                    {g.ohne.filter(sichtbar).map(x => personZeile(x, false))}
                  </>
                )}
              </div>
            ))}
            {zusatz.filter(sichtbar).length > 0 && (
              <>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', padding: '6px 4px 0' }}>Zugeordnet</span>
                {zusatz.filter(sichtbar).map(x => personZeile(x, false))}
              </>
            )}
            {alleSichtbaren.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--ink3)', padding: 8 }}>
                {q ? 'Niemand gefunden.' : `Keine ${ehemalige ? '' : 'aktuellen '}${WORT[portal].mehrzahl} zu ${objektIds.length ? 'diesem Objekt' : 'diesem Dokument'}.`}
              </span>
            )}
          </div>

          {warnen && (
            <div role="alert" className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 14, background: 'var(--bad-soft, var(--fill))', boxShadow: 'inset 0 0 0 1px var(--bad)' }}>
              <span className="flex items-center" style={{ gap: 8, fontSize: 13, fontWeight: 800, color: 'var(--bad)' }}>
                <Warning size={16} weight="fill" />Persönliches Dokument an mehrere Empfänger
              </span>
              <span style={{ fontSize: 12, lineHeight: 1.45 }}>
                {persoenlich.length === 1 ? `„${persoenlich[0]!.titel}“ wirkt persönlich` : `${persoenlich.length} Dokumente wirken persönlich`} (z. B. Mietvertrag, Abrechnung, Mahnung oder einer Person zugeordnet).
                Bitte prüfen, ob wirklich {auswahl.mieter.alle || auswahl.eigentuemer.alle ? 'alle' : 'mehrere Personen'} es sehen sollen.
              </span>
              <span className="flex items-center" style={{ gap: 8 }}>
                <AuswahlKaestchen an={trotzdem} label="Trotzdem freigeben" onUmschalten={() => setTrotzdem(v => !v)} />
                <span style={{ fontSize: 13, fontWeight: 700 }}>Ja, trotzdem so freigeben</span>
              </span>
            </div>
          )}
          {aendern && dokumente.length > 1 && (
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Die bisherigen Freigaben der {dokumente.length} Dokumente werden durch diese Auswahl ersetzt.</span>
          )}
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Es wird nichts verschickt – die Empfänger sehen die Dokumente nach dem Login in ihrem Portal.</span>
          {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
        </>
      )}
    </DialogHuelle>
  );
}
